<template>
    <div>
        <b-form-group :label="$t('renameNameLabel')">
            <b-input v-model.trim="newName" :placeholder="$t('folderConfig.renamePlaceholder')"
            :state="!$v.newName.$invalid"></b-input>
            <b-form-invalid-feedback v-html="nameFeedback"></b-form-invalid-feedback>
        </b-form-group>
        <b-form-group :label="$t('renameParentLabel')">
            <div class="folderlist" tabindex="0" @keydown="doKeyboard($event)" role="listbox"
                    :aria-label="$t('ariaRenameFolderList')">
                <div v-for="(f,ind) in folderTree" :key="ind" :ref="`fld-${ind}`"
                        :class="{ disabled: f.disabled, selected: ind === selectIndex,
                        origParent: f.value === originalParent }"
                        class="item"
                        @click="clickEntry(ind)" role="option"
                        :aria-selected="ind === selectIndex">
                <span :style="`padding-left:${f.indent}px`">
                    {{f.text}}
                </span>
                </div>
            </div>
        </b-form-group>
        {{$t('newPath', { path: decodedNewValue })}}
    </div>
</template>

<script>
import FolderSorter from '../helpers/folderSorting';
import store from '@/store';
import { imapDecode, imapEncode } from 'emailjs-utf7';
import { helpers, maxLength, required } from 'vuelidate/lib/validators';

const pathValidator = helpers.regex('path', /^[^/]+$/);

export default {
  name: 'renameDialog',
  data: function() {
    return {
      levelPadding: 10,
      selectIndex: 0,
      originalParent: '',
      newName: ''
    };
  },
  props: {
    moveFolder: String
  },
  validations: {
    newName: {
      required,
      maxLength: maxLength(50),
      pathValidator
    }
  },
  watch: {
    newName(newVal) {
      this.emitValue();
    },
    selectIndex(newVal) {
      this.emitValue();
    }
  },
  mounted() {
    const i = this.moveFolder.lastIndexOf('/');
    if (i === -1) {
      this.originalParent = '';
    } else {
      this.originalParent = this.moveFolder.substr(0, i);
    }
    this.newName = imapDecode(this.moveFolder.substr(i + 1));
    this.$root.$once('bv::modal::shown', (bvEvent, modalId) => {
      console.log('Modal is about to be shown', bvEvent, modalId);
      this.$nextTick(() => {
        const ind = this.folderTree.findIndex(f => f.value === this.originalParent);
        if (ind !== -1) {
          this.selectIndex = ind;
          console.log('scroll to ', ind, Object.keys(this.$refs));
          this.$refs[`fld-${this.selectIndex}`][0].scrollIntoView({ behaviour: 'smooth', block: 'nearest' });
        }
      });
    });
  },
  methods: {
    emitValue() {
      let invalid = this.$v.$invalid;
      console.log('emit', this.newValue, invalid);
      this.$emit('input', { name: this.newValue, invalid });
    },
    clickEntry(ind) {
      if (!this.folderTree[ind].disabled) {
        this.selectIndex = ind;
      }
    },
    doKeyboard(evt) {
      const oldSelect = this.selectIndex;
      if (evt.key === 'ArrowDown') {
        this.selectIndex++;
        evt.preventDefault();
      } else if (evt.key === 'ArrowUp') {
        this.selectIndex--;
        evt.preventDefault();
      } else if (evt.key === 'PageDown') {
        this.selectIndex += 10;
      } else if (evt.key === 'PageUp') {
        this.selectIndex -= 10;
      } else if (evt.key === 'Home') {
        this.selectIndex = 0;
      } else if (evt.key === 'End') {
        this.selectIndex = this.folderTree.length - 1;
      } else if (evt.key === 'Escape') {
        this.$emit('hide');
      } else if (((evt.key === 'Enter') || (evt.key === 'Space')) && (this.selectIndex >= 0) &&
        (this.selectIndex < this.folderTree.length)) {
        this.$emit('select', { targetFolder: this.folderTree[this.selectIndex] });
      }
      if (this.selectIndex >= this.folderTree.length) {
        this.selectIndex = this.folderTree.length - 1;
      }
      if (this.selectIndex < 0) {
        this.selectIndex = 0;
      }
      evt.stopPropagation();

      if ((oldSelect !== this.selectIndex) && (this.folderTree.length > 0)) {
        this.$refs[`fld-${this.selectIndex}`][0].scrollIntoView({ behaviour: 'smooth', block: 'nearest' });
      }
    },
    getSortedTree(folder, result, indent) {
      if ((folder.id === -1) || (folder.id === -2)) {
        return;
      }
      if (indent >= this.levelPadding) {
        let disabled = folder.rights.indexOf('k') === -1;
        if ((folder.name === this.moveFolder) ||
          (folder.name.substr(0, this.moveFolder.length + 1) === `${this.moveFolder}/`)) {
          disabled = true;
        }
        result.push({ text: `${folder.dispName}`,
          value: folder.name,
          indent,
          disabled,
          parent: folder.parent,
          id: folder.id
        });
      }
      if (Object.keys(folder.children).length > 0) {
        for (let name of FolderSorter(folder.children, folder.parent === null)) {
          this.getSortedTree(folder.children[name], result, indent + this.levelPadding);
        }
      }
      return result;
    }
  },
  computed: {
    folderTree() {
      const rootFolder = {
        text: this.$t('general:text.rootFolder'),
        value: '',
        indent: 0,
        disabled: false,
        parent: null,
        id: 0
      };
      return this.getSortedTree(store.state.folders, [rootFolder], 0); // -this.levelPadding);
    },
    newValue() {
      if (this.selectIndex === 0) {
        return `${imapEncode(this.newName)}`;
      }
      return `${this.folderTree[this.selectIndex].value}/${imapEncode(this.newName)}`;
    },
    decodedNewValue() {
      return imapDecode(this.newValue);
    },
    nameFeedback() {
      let fb = '';
      console.log(this.$v.newName);
      if (!this.$v.newName.required) {
        fb += this.$t('validErrors.renameNameRequired');
      }
      if (!this.$v.newName.maxLength) {
        fb += this.$t('validErrors.renameNameTooLong', { maxchars: this.$v.newName.$params.maxLength.max });
      }
      if (!this.$v.newName.pathValidator) {
        fb += this.$t('validErrors.renameNameInvalidChar');
      }
      return fb;
    }
  }
};
</script>

<style scoped lang="scss">
    @import '../assets/bootstrap-style.scss';

    .folderlist {
        height: 30vh;
        overflow-y: auto;
        border: 1px solid $generic-box-border;
        .item {
            // padding: 5px 5px 5px 5px;
            cursor: pointer;
            user-select: none;
            &.selected {
                background-color: $generic-select-background;
            }
            &:hover {
                background-color: $generic-hover-background;
            }
            &.disabled {
                color: $gray-500;
                cursor: inherit;
            }
            &.origParent {
                font-weight: bold;
            }

        }
    }

</style>
